import { atom, getDefaultStore, useAtomValue, useSetAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { atomFamily } from 'jotai-family'

export type TPinBuryState = 'pinned' | 'buried' | null

type TPinBuryMap = Record<string, 'pinned' | 'buried'>

export const pinBuryMapAtom = atomWithStorage<TPinBuryMap>('s:pinBury', {}, undefined, {
  getOnInit: true
})

// per-pubkey reactive atom. Pass the same pubkey string to observe its state.
export const pinBuryStateAtom = atomFamily((pubkey: string) =>
  atom<TPinBuryState>((get) => get(pinBuryMapAtom)[pubkey] ?? null)
)

const store = getDefaultStore()

// imperative accessor (mirrors the old usePinBury().getPinBuryState API); reads
// the current store snapshot without subscribing.
export function getPinBuryState(pubkey: string): TPinBuryState {
  return store.get(pinBuryMapAtom)[pubkey] ?? null
}

export function setPinned(pubkey: string) {
  store.set(pinBuryMapAtom, (prev) => ({ ...prev, [pubkey]: 'pinned' }))
}

export function setBuried(pubkey: string) {
  store.set(pinBuryMapAtom, (prev) => ({ ...prev, [pubkey]: 'buried' }))
}

export function clearPinBuryState(pubkey: string) {
  store.set(pinBuryMapAtom, (prev) => {
    if (!(pubkey in prev)) return prev
    const next = { ...prev }
    delete next[pubkey]
    return next
  })
}

// hook helpers for components that want reactive reads / writes
export function usePinBuryState(pubkey: string): TPinBuryState {
  return useAtomValue(pinBuryStateAtom(pubkey))
}

export function usePinBuryActions() {
  const setMap = useSetAtom(pinBuryMapAtom)
  return {
    setPinned: (pubkey: string) => setMap((prev) => ({ ...prev, [pubkey]: 'pinned' })),
    setBuried: (pubkey: string) => setMap((prev) => ({ ...prev, [pubkey]: 'buried' })),
    clearState: (pubkey: string) =>
      setMap((prev) => {
        if (!(pubkey in prev)) return prev
        const next = { ...prev }
        delete next[pubkey]
        return next
      })
  }
}
